/**
 * /velg-passord — «Velg ditt eget passord» (#143).
 *
 * Adminer fra før #140 logger fortsatt inn med det gamle felles
 * husstandspassordet; det står i `members.password_hash` uten
 * `password_changed_at`, og serveren melder `must_change_password`. Alle som
 * arvet det, deler det, så det skal ikke kunne brukes til noe annet enn å
 * velge et eget: serveren avviser adminopplåsingen (403
 * `password_change_required`), og `RequireOwnPassword` sender alle andre
 * flater hit. Utlogging virker fortsatt.
 *
 * Passordbyttet går gjennom samme skjema som Innstillinger → Konto
 * (`ChangePasswordForm`), bare åpent og uten Avbryt.
 */
import type { ReactElement, ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChangePasswordForm, LogoutButton } from '../settings/AccountSection';
import { SESSION_KEY, useSession } from './useSession';

export const CHOOSE_PASSWORD_PATH = '/velg-passord';

/** Veiviseren for en familie systemadminen nettopp har opprettet (FF-11, #179). */
export const FAMILY_SETUP_PATH = '/onboarding/step-1';

/**
 * Sender et medlem som må velge eget passord til /velg-passord, og en admin
 * i en familie som ikke er ferdig satt opp til veiviseren (FF-11, #179). Mens
 * økten lastes (eller uten økt — det ambiente dashbordet) vises innholdet
 * som før.
 */
export function RequireOwnPassword({ children }: { children: ReactNode }): ReactElement {
  const { data } = useSession();
  if (data?.member?.must_change_password) return <Navigate to={CHOOSE_PASSWORD_PATH} replace />;
  if (data?.member?.family_setup_pending) return <Navigate to={FAMILY_SETUP_PATH} replace />;
  return <>{children}</>;
}

export function ChooseOwnPasswordRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <main className="flex min-h-screen items-center justify-center p-12">
        <p data-testid="choose-password-loading" className="text-ink-2 text-sm">
          {t('choose_password.loading')}
        </p>
      </main>
    );
  }
  const member = session?.member;
  if (!member) return <Navigate to="/login" replace />;
  if (!member.must_change_password) return <Navigate to="/" replace />;

  async function onDone(): Promise<void> {
    await qc.invalidateQueries({ queryKey: SESSION_KEY });
    navigate('/', { replace: true });
  }

  return (
    <main
      data-route="choose-password"
      className="flex min-h-screen flex-col items-center justify-start gap-6 p-6 sm:p-12"
    >
      <div className="w-full max-w-md space-y-6">
        <h1 className="font-display text-display-s text-ink">{t('choose_password.title')}</h1>
        <p data-testid="choose-password-body" className="text-ink-2 text-sm">
          {t('choose_password.body')}
        </p>
        <ChangePasswordForm
          username={member.username ?? null}
          alwaysOpen
          onDone={() => void onDone()}
          submitLabel={t('choose_password.submit')}
          currentLabel={t('choose_password.current')}
        />
        <LogoutButton />
      </div>
    </main>
  );
}
